import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePauseCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../../services";
import masterDataService from "../../../services/masterDataService";
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

//Holiday Calendar Page
const HolidayCalendarPage = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    description: "",
    isWorkingDay: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadHolidays();
    } else {
      setError("Please login to view holiday calendar");
      setLoading(false);
    }
  }, [tenantId]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await masterDataService.getHolidayCalendar(tenantId);
      setHolidays(data || []);
    } catch (err) {
      setError("Failed to load holiday calendar. Please try again.");
      console.error("Error loading holiday calendar:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredHolidays = holidays.filter((holiday) =>
    holiday.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingHoliday(null);
    setFormData({
      date: "",
      description: "",
      isWorkingDay: false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toISOString().split("T")[0];
    };
    setFormData({
      date: formatDate(holiday.date),
      description: holiday.description || "",
      isWorkingDay: holiday.isWorkingDay || false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError("Delete functionality is not available for holiday calendar.");
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete holiday. Please try again.");
      console.error("Error deleting holiday:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.date) {
      errors.date = "Date is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const holidayData = {
        tenantId: tenantId,
        date: new Date(formData.date),
        description: formData.description.trim() || null,
        isWorkingDay: formData.isWorkingDay,
      };

      if (editingHoliday) {
        holidayData.calendarId = editingHoliday.calendarId;
        await masterDataService.updateHolidayCalendar(
          editingHoliday.calendarId,
          holidayData,
        );
      } else {
        await masterDataService.createHolidayCalendar(holidayData);
      }

      await loadHolidays();
      setShowModal(false);
      setFormData({ date: "", description: "", isWorkingDay: false });
      setEditingHoliday(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save holiday. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ date: "", description: "", isWorkingDay: false });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading holiday calendar..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Holiday Calendar</h1>
          <p>Manage holiday calendar for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Holiday
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search holidays..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredHolidays.length}{" "}
            {filteredHolidays.length === 1 ? "holiday" : "holidays"}
          </div>
        </div>

        {filteredHolidays.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePauseCircle className="empty-icon" />
            <h3>No holidays found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first holiday"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Holiday
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Description</th>
                <th>Working Day</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHolidays.map((holiday) => (
                <tr key={holiday.calendarId}>
                  <td>{holiday.calendarId}</td>
                  <td>
                    {holiday.date
                      ? new Date(holiday.date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="name-cell">{holiday.description || "-"}</td>
                  <td>{holiday.isWorkingDay ? "Yes" : "No"}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(holiday)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingHoliday ? "Edit Holiday" : "Add New Holiday"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="holiday-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingHoliday
                  ? "Updating..."
                  : "Creating..."
                : editingHoliday
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="holiday-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="date">
                Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className={formErrors.date ? "error" : ""}
              />
              {formErrors.date && (
                <span className="field-error">{formErrors.date}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="isWorkingDay" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  id="isWorkingDay"
                  checked={formData.isWorkingDay}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isWorkingDay: e.target.checked,
                    })
                  }
                  style={{ width: 'auto', minHeight: 'auto' }}
                />
                Is Working Day
              </label>
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default HolidayCalendarPage;
